import React from 'react';

import { isFeatureEnabled } from '@lumapps/features';
import { getCurrentInstance } from '@lumapps/instance/ducks/selectors';
import { JOURNEYS_FF } from '@lumapps/journeys/constants';
import { getLanguageHeader } from '@lumapps/languages';
import { NAVIGATION } from '@lumapps/navigation/keys';
import { NavigationItemType, NavigationMap } from '@lumapps/navigation/types';
import { PLAY_VIDEO_FEATURE_TOKEN } from '@lumapps/play-core/constants';
import { useSelector } from '@lumapps/redux/react';
import { RedirectToNotFound } from '@lumapps/router/components/RedirectToNotFound';
import { useTranslate } from '@lumapps/translations';

import { getMainNavigationMaps, updateMainNavigationMaps } from '../../api';
import { MAIN_NAVIGATION_SYSTEM_PAGES } from '../../constants';
import {
    hasMainNavAdminAccess,
    hasMenuDeleteRights,
    hasMenuDropRights,
    hasMenuEditRights,
} from '../../ducks/selectors';
import { NAVIGATION_ADMIN } from '../../keys';
import { ALL_AVAILABLE_FIELDS_OBJECT, DEFAULT_ENABLED_OPTIONS, NavigationAdminConfiguration } from '../../types';
import { NavigationAdmin } from '../NavigationAdmin';

export const mainNavigationAdminConfiguration: NavigationAdminConfiguration = {
    availableFilters: {
        group: true,
        language: true,
        hideFiltered: true,
    },
    // All fields are available on the main nav admin
    availableEditableNodeFields: ALL_AVAILABLE_FIELDS_OBJECT,
    // Every node of the main navigation can have children
    isListNode: () => true,
    // Prevent the navigation admin to automatically open empty sections on init
    shouldOpenEmptySectionsOnInit: false,
    // Main navigation fetches data at instance level
    fetchAtInstanceLevel: true,
    // Don't validate external url
    validateExternalUrl: false,
};

/**
 * The Main Navigation admin page of the back-office.
 */
export const MainNavigationAdminPage: React.FC = () => {
    const { translateKey } = useTranslate();
    const canAccessMainNavAdmin = useSelector(hasMainNavAdminAccess);
    const isNavigationSystemPagesEnabled = useSelector(isFeatureEnabled(MAIN_NAVIGATION_SYSTEM_PAGES));
    const isJourneyEnabled = useSelector(isFeatureEnabled(JOURNEYS_FF));
    const isVideoGalleryEnabled = useSelector(isFeatureEnabled(PLAY_VIDEO_FEATURE_TOKEN));

    const { id: siteId } = useSelector(getCurrentInstance);
    const acceptLanguageHeader = useSelector(getLanguageHeader);

    const hasAdminOrMenuEditRole = useSelector(hasMenuEditRights);
    const hasAdminOrMenuDeleteRole = useSelector(hasMenuDeleteRights);
    const hasAdminOrMenuDropRole = useSelector(hasMenuDropRights);

    const getNavigationMap = React.useCallback(
        () => getMainNavigationMaps(siteId, acceptLanguageHeader),
        [acceptLanguageHeader, siteId],
    );
    const updateNavigationMap = React.useCallback(
        (navigationMap: NavigationMap) => updateMainNavigationMaps(siteId, navigationMap, acceptLanguageHeader),
        [acceptLanguageHeader, siteId],
    );

    const wizardOptions = React.useMemo(
        () => ({
            ...DEFAULT_ENABLED_OPTIONS,
            [NavigationItemType.CUSTOM_LIST]: true,
            [NavigationItemType.DIRECTORY]: true,
            [NavigationItemType.USER_DIRECTORY]: true,
            [NavigationItemType.JOURNEYS]: (isNavigationSystemPagesEnabled && isJourneyEnabled) || null,
            [NavigationItemType.PERSONAL_FEED]: isNavigationSystemPagesEnabled || null,
            [NavigationItemType.VIDEO_GALLERY]: (isNavigationSystemPagesEnabled && isVideoGalleryEnabled) || null,
        }),
        [isJourneyEnabled, isNavigationSystemPagesEnabled, isVideoGalleryEnabled],
    );

    if (!canAccessMainNavAdmin) {
        return <RedirectToNotFound />;
    }

    return (
        <NavigationAdmin
            emptyTitle={translateKey(NAVIGATION_ADMIN.EMPTY_LIST_TITLE)}
            emptyMessage={translateKey(NAVIGATION_ADMIN.EMPTY_LIST_DESCRIPTION)}
            movedToFirstLevelKeyMessage={NAVIGATION.MOVED_TO_THE_FIRST_LEVEL}
            moveNodeRestrictionMessage={translateKey(NAVIGATION_ADMIN.NOT_ALLOWED_TO_ADD_CONTENT_TO_SECTION)}
            headerTitle={translateKey(NAVIGATION.TITLE)}
            scope="admin-main-navigation"
            getNavigationMap={getNavigationMap}
            updateNavigationMap={updateNavigationMap}
            configuration={mainNavigationAdminConfiguration}
            wizardOptions={wizardOptions}
            // apply specific user permissions related to MENU_XXX roles
            userPermissions={{
                canAddNode: hasAdminOrMenuEditRole || hasAdminOrMenuDropRole,
                canDeleteNode: hasAdminOrMenuDeleteRole || hasAdminOrMenuDropRole,
                canEditNode: hasAdminOrMenuEditRole || hasAdminOrMenuDropRole,
            }}
        />
    );
};

MainNavigationAdminPage.displayName = 'MainNavigationAdminPage';
